/**
 * Crop Simulator Utility
 * Generates different aspect ratio crops from an image and bounding box
 */

// Define unique aspect ratios with all applicable platforms
export const ASPECT_RATIO_GROUPS = {
  square_1_1: {
    ratio: [1, 1],
    displayName: 'Square',
    aspectRatio: '1:1',
    platforms: [
      { name: 'Instagram', icon: 'instagram', use_case: 'Instagram Post' },
      { name: 'Facebook', icon: 'facebook', use_case: 'Facebook Post' },
      { name: 'LinkedIn', icon: 'linkedin', use_case: 'LinkedIn Post' }
    ]
  },
  portrait_9_16: {
    ratio: [9, 16],
    displayName: 'Portrait',
    aspectRatio: '9:16',
    platforms: [
      { name: 'Instagram', icon: 'instagram', use_case: 'Stories & Reels' },
      { name: 'TikTok', icon: 'mobile', use_case: 'TikTok Video' },
      { name: 'Snapchat', icon: 'mobile', use_case: 'Snapchat Story' },
      { name: 'Mobile', icon: 'mobile', use_case: 'Mobile Wallpaper' }
    ]
  },
  landscape_16_9: {
    ratio: [16, 9],
    displayName: 'Landscape',
    aspectRatio: '16:9',
    platforms: [
      { name: 'YouTube', icon: 'desktop', use_case: 'YouTube Thumbnail' },
      { name: 'Desktop', icon: 'desktop', use_case: 'Desktop Display' },
      { name: 'Presentation', icon: 'presentation', use_case: 'Slides' },
      { name: 'TV', icon: 'desktop', use_case: 'TV Display' }
    ]
  },
  landscape_1_91_1: {
    ratio: [1.91, 1],
    displayName: 'Wide',
    aspectRatio: '1.91:1',
    platforms: [
      { name: 'Facebook', icon: 'facebook', use_case: 'Facebook Link' },
      { name: 'LinkedIn', icon: 'linkedin', use_case: 'LinkedIn Link' }
    ]
  },
  twitter_header_3_1: {
    ratio: [3, 1],
    displayName: 'Banner',
    aspectRatio: '3:1',
    platforms: [
      { name: 'Twitter', icon: 'twitter', use_case: 'Twitter Header' }
    ]
  },
  linkedin_cover_4_1: {
    ratio: [4, 1],
    displayName: 'Cover',
    aspectRatio: '4:1',
    platforms: [
      { name: 'LinkedIn', icon: 'linkedin', use_case: 'LinkedIn Banner' }
    ]
  },
  portrait_4_5: {
    ratio: [4, 5],
    displayName: 'Tall',
    aspectRatio: '4:5',
    platforms: [
      { name: 'Instagram', icon: 'instagram', use_case: 'Instagram Portrait' },
      { name: 'Facebook', icon: 'facebook', use_case: 'Facebook Portrait' }
    ]
  },
  cinema_21_9: {
    ratio: [21, 9],
    displayName: 'Cinematic',
    aspectRatio: '21:9',
    platforms: [
      { name: 'Cinema', icon: 'cinema', use_case: 'Cinematic Display' },
      { name: 'YouTube', icon: 'desktop', use_case: 'Ultrawide Video' }
    ]
  }
};

// Keep old ASPECT_RATIOS for backward compatibility if needed
export const ASPECT_RATIOS = ASPECT_RATIO_GROUPS;

/**
 * Calculate crop dimensions for a given aspect ratio
 * Centers the bounding box within the crop
 * 
 * @param {number} imgWidth - Original image width
 * @param {number} imgHeight - Original image height
 * @param {Array} bbox - Bounding box [x1, y1, x2, y2]
 * @param {Array} aspectRatio - Target aspect ratio [width, height]
 * @param {number} padding - Padding percentage (0.1 = 10%)
 * @returns {Object} Crop dimensions and position
 */
function calculateCropDimensions(imgWidth, imgHeight, bbox, aspectRatio, padding = 0.1) {
  const [x1, y1, x2, y2] = bbox;
  const bboxWidth = x2 - x1;
  const bboxHeight = y2 - y1;
  
  // Add padding to bounding box
  const paddedWidth = bboxWidth * (1 + padding);
  const paddedHeight = bboxHeight * (1 + padding);
  
  // Calculate target aspect ratio
  const targetRatio = aspectRatio[0] / aspectRatio[1];
  const currentRatio = paddedWidth / paddedHeight;
  
  let cropWidth, cropHeight;
  
  if (currentRatio > targetRatio) {
    // Bounding box is wider than target ratio
    cropWidth = paddedWidth;
    cropHeight = cropWidth / targetRatio;
  } else {
    // Bounding box is taller than target ratio
    cropHeight = paddedHeight;
    cropWidth = cropHeight * targetRatio;
  }
  
  // Center the bounding box in the crop
  const bboxCenterX = (x1 + x2) / 2;
  const bboxCenterY = (y1 + y2) / 2;
  
  let cropX = Math.round(bboxCenterX - cropWidth / 2);
  let cropY = Math.round(bboxCenterY - cropHeight / 2);
  
  // Ensure crop stays within image bounds
  cropX = Math.max(0, Math.min(cropX, imgWidth - cropWidth));
  cropY = Math.max(0, Math.min(cropY, imgHeight - cropHeight));
  
  // Adjust crop size if it exceeds image bounds while maintaining aspect ratio
  if (cropX + cropWidth > imgWidth || cropY + cropHeight > imgHeight) {
    // Calculate how much we need to scale down
    const scaleX = cropX + cropWidth > imgWidth ? (imgWidth - cropX) / cropWidth : 1;
    const scaleY = cropY + cropHeight > imgHeight ? (imgHeight - cropY) / cropHeight : 1;
    const scale = Math.min(scaleX, scaleY);
    
    // Scale both dimensions equally to maintain aspect ratio
    cropWidth = cropWidth * scale;
    cropHeight = cropHeight * scale;
  }
  
  return {
    x: cropX,
    y: cropY,
    width: Math.round(cropWidth),
    height: Math.round(cropHeight),
    needsPadding: cropX < 0 || cropY < 0 || 
                   cropX + cropWidth > imgWidth || 
                   cropY + cropHeight > imgHeight
  };
}

/**
 * Generate a cropped image for a specific aspect ratio
 * 
 * @param {HTMLImageElement|string} imageSource - Original image or base64 string
 * @param {Array} bbox - Bounding box [x1, y1, x2, y2]
 * @param {Array} aspectRatio - Target aspect ratio [width, height]
 * @param {number} outputSize - Max dimension for output (default 1024)
 * @returns {Promise<string>} Base64 encoded cropped image
 */
export async function generateCrop(imageSource, bbox, aspectRatio, outputSize = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const cropDims = calculateCropDimensions(
        img.width, 
        img.height, 
        bbox, 
        aspectRatio
      );
      
      // Calculate output dimensions
      const targetRatio = aspectRatio[0] / aspectRatio[1];
      let outWidth, outHeight;
      
      if (targetRatio > 1) {
        outWidth = outputSize;
        outHeight = Math.round(outputSize / targetRatio);
      } else {
        outHeight = outputSize;
        outWidth = Math.round(outputSize * targetRatio);
      }
      
      // Create canvas for cropping
      const canvas = document.createElement('canvas');
      canvas.width = outWidth;
      canvas.height = outHeight;
      const ctx = canvas.getContext('2d');
      
      // Fill with transparent background
      ctx.fillStyle = 'rgba(0, 0, 0, 0)';
      ctx.fillRect(0, 0, outWidth, outHeight);
      
      // Draw the cropped portion of the image
      if (cropDims.needsPadding) {
        // Center the available image data if padding is needed
        const scale = Math.min(
          outWidth / cropDims.width,
          outHeight / cropDims.height
        );
        const scaledWidth = cropDims.width * scale;
        const scaledHeight = cropDims.height * scale;
        const offsetX = (outWidth - scaledWidth) / 2;
        const offsetY = (outHeight - scaledHeight) / 2;
        
        ctx.drawImage(
          img,
          cropDims.x, cropDims.y, cropDims.width, cropDims.height,
          offsetX, offsetY, scaledWidth, scaledHeight
        );
      } else {
        // Normal crop without padding
        ctx.drawImage(
          img,
          cropDims.x, cropDims.y, cropDims.width, cropDims.height,
          0, 0, outWidth, outHeight
        );
      }
      
      // Convert to base64
      canvas.toBlob(
        (blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Remove data URL prefix to get pure base64
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.9
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Load the image
    if (typeof imageSource === 'string') {
      // If it's a base64 string, add data URL prefix if needed
      if (imageSource.startsWith('data:')) {
        img.src = imageSource;
      } else {
        img.src = `data:image/jpeg;base64,${imageSource}`;
      }
    } else {
      // If it's already an image element
      img.src = imageSource.src;
    }
  });
}

/**
 * Generate all aspect ratio variations for an image
 * 
 * @param {string} imageBase64 - Original image as base64
 * @param {Array} bbox - Bounding box [x1, y1, x2, y2]
 * @returns {Promise<Array>} Array of crop objects
 */
export async function generateAllCrops(imageBase64, bbox) {
  const crops = [];
  
  for (const [key, group] of Object.entries(ASPECT_RATIO_GROUPS)) {
    try {
      const cropBase64 = await generateCrop(
        imageBase64,
        bbox,
        group.ratio
      );
      
      crops.push({
        name: key,
        filename: `${key}.jpg`,
        path: `data:image/jpeg;base64,${cropBase64}`,
        displayName: group.displayName,
        aspectRatio: group.aspectRatio,
        platforms: group.platforms,
        base64: cropBase64,
        // For backward compatibility, keep single platform data from first platform
        ratioName: group.platforms[0]?.use_case || group.displayName,
        platform: group.platforms[0]?.name || group.displayName,
        icon: group.platforms[0]?.icon || 'image'
      });
    } catch (error) {
      console.error(`Failed to generate crop for ${key}:`, error);
    }
  }
  
  return crops;
}